/**
 * RectAreaLightComponent - emits light from a rectangular surface.
 * Wraps THREE.RectAreaLight. Does not cast shadows (no-op override).
 */
import * as THREE from 'three';
import { RectAreaLightHelper } from 'three/addons/helpers/RectAreaLightHelper.js';

import { EngineClass } from '../../systems/ClassRegistry.js';
import { property } from '../../utils/serialization/decorator.js';

import { LightComponent } from './LightComponent.js';

import type { LightComponentOptions } from './LightComponent.js';



export interface RectAreaLightComponentOptions extends LightComponentOptions {
  /** Width of the light rectangle */
  width?: number;
  /** Height of the light rectangle */
  height?: number;
}

/** Wraps a THREE.RectAreaLight. */
@EngineClass('RectAreaLightComponent')
export class RectAreaLightComponent extends LightComponent<THREE.RectAreaLight, RectAreaLightComponentOptions> {

  @property({ transient: true })
  get width(): number {
    return this.light?.width ?? 10;
  }
  set width(value: number) {
    if (this.light) {
      this.light.width = value;
    }
    this.updateHelperForSizeChange();
  }

  @property({ transient: true })
  get height(): number {
    return this.light?.height ?? 10;
  }
  set height(value: number) {
    if (this.light) {
      this.light.height = value;
    }
    this.updateHelperForSizeChange();
  }

  protected override createLight(): THREE.RectAreaLight {
    return new THREE.RectAreaLight();
  }

  public override initialize(options?: RectAreaLightComponentOptions): void {
    super.initialize(options);
    this.width = options?.width ?? this.width;
    this.height = options?.height ?? this.height;
  }

  /**
   * Update the helper for size changes. RectAreaLightHelper has no update method,
   * so we recreate it to reflect size changes.
   */
  private updateHelperForSizeChange(): void {
    if (this.lightHelper) {
      this.recreateLightHelper();
    }
  }

  /** RectArea lights don't cast shadows. */
  protected override configureShadow(): this {
    // RectArea lights don't cast shadows, so this is a no-op
    return this;
  }

  protected override createLightHelper(): THREE.Object3D | null {
    if (this.light) {
      return new RectAreaLightHelper(this.light);
    }
    return null;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_AreaLight';
  }
}
